// ---------------------------------------------------------------------------
// AbsoluteBlockWrapper — draggable wrapper for absolute-positioned blocks.
//
// Handles drag-to-move with:
// - 3px manhattan distance threshold to distinguish click from drag
// - Zoom-corrected mouse deltas (works at any zoom level)
// - localPosRef pattern to avoid stale closure in mouseUp callback
// - Commits position to store only on mouseUp (not every mouseMove)
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { useEditorStore } from '@/features/page-editor/store/editor-store'
import type { AbsoluteBlockPosition } from '@cogo/types-v2'
import { BlockResizeHandles } from './block-resize-handles'

interface AbsoluteBlockWrapperProps {
  blockName: string
  position: AbsoluteBlockPosition
  isPreview: boolean
  isSelected: boolean
  children: ReactNode
}

const DRAG_THRESHOLD = 3 // manhattan distance in screen pixels

export function AbsoluteBlockWrapper({
  blockName,
  position,
  isPreview,
  isSelected,
  children,
}: AbsoluteBlockWrapperProps) {
  const [localPos, setLocalPos] = useState({ x: position.x, y: position.y })
  const localPosRef = useRef(localPos)

  // Sync from prop when position changes externally (e.g. undo/redo)
  useEffect(() => {
    setLocalPos({ x: position.x, y: position.y })
    localPosRef.current = { x: position.x, y: position.y }
  }, [position.x, position.y])

  const dragRef = useRef<{
    startMX: number
    startMY: number
    startX: number
    startY: number
    dragging: boolean
  } | null>(null)

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (isPreview) return
      // Only respond to left mouse button
      if (e.button !== 0) return
      e.stopPropagation()

      dragRef.current = {
        startMX: e.clientX,
        startMY: e.clientY,
        startX: localPosRef.current.x,
        startY: localPosRef.current.y,
        dragging: false,
      }
    },
    [isPreview],
  )

  // Window-level mousemove + mouseup listeners for drag
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!dragRef.current) return

      const zoom = useEditorStore.getState().zoom || 1
      const dx = (e.clientX - dragRef.current.startMX) / zoom
      const dy = (e.clientY - dragRef.current.startMY) / zoom

      // Check drag threshold (manhattan distance in screen space)
      if (!dragRef.current.dragging) {
        const screenDist =
          Math.abs(e.clientX - dragRef.current.startMX) +
          Math.abs(e.clientY - dragRef.current.startMY)
        if (screenDist < DRAG_THRESHOLD) return
        dragRef.current.dragging = true
        document.body.style.cursor = 'grabbing'
        document.body.style.userSelect = 'none'
      }

      const newPos = {
        x: dragRef.current.startX + dx,
        y: dragRef.current.startY + dy,
      }
      setLocalPos(newPos)
      localPosRef.current = newPos
    }

    const handleMouseUp = () => {
      if (!dragRef.current) return

      if (dragRef.current.dragging) {
        // Commit position to store
        const store = useEditorStore.getState()
        const activePageId = store.activePageId
        if (activePageId) {
          store.updateBlockPosition(activePageId, blockName, {
            x: Math.round(localPosRef.current.x),
            y: Math.round(localPosRef.current.y),
          })
        }
      }

      dragRef.current = null
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }

    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseup', handleMouseUp)

    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
    }
  }, [blockName])

  return (
    <div
      style={{
        position: 'absolute',
        left: localPos.x,
        top: localPos.y,
        width: position.w,
        height: position.h,
        overflow: 'hidden',
        cursor: isPreview ? 'default' : 'grab',
      }}
      onMouseDown={handleMouseDown}
      data-block-name={blockName}
      data-testid={`absolute-block-${blockName}`}
    >
      {children}
      {isSelected && !isPreview && (
        <BlockResizeHandles
          blockName={blockName}
          position={position}
          width={position.w}
          height={position.h}
        />
      )}
    </div>
  )
}
